import React, { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import API from '../../api';
import Button from '../../components/common/Button';
import { showConfirm } from '../../utils/popup';
import { formatDateTime } from '../../utils/regionalFormat';
import ListPageLayout, { ListPageHeader, ListLoadingSpinner } from '../../components/layout/ListPageLayout';
import { apiErrorMessage } from '../../utils/apiError';

export default function StudentImportPage() {
    const { t } = useTranslation();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState(null);

    const loadStatus = async () => {
        try {
            setLoading(true);
            setMessage(null);
            const response = await API.get('/admin/student-import/status');
            setStatus(response.data);
        } catch (error) {
            console.error('Failed to load import status:', error);
            setStatus(null);
            setMessage({ type: 'error', text: t('pages.studentImport.loadFailed') });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStatus();
    }, []);

    const handleTriggerImport = async () => {
        if (!await showConfirm(t('pages.studentImport.confirmTrigger'))) {
            return;
        }

        try {
            setProcessing(true);
            setMessage(null);
            await API.post('/admin/student-import/trigger');
            setMessage({ type: 'success', text: t('pages.studentImport.triggerSuccess') });
            setTimeout(loadStatus, 3000);
        } catch (error) {
            console.error('Failed to trigger import:', error);
            setMessage({ type: 'error', text: apiErrorMessage(error, t('pages.studentImport.triggerFailed')) });
        } finally {
            setProcessing(false);
        }
    };

    const handleResetTracker = async () => {
        if (!await showConfirm(t('pages.studentImport.confirmReset'))) {
            return;
        }

        try {
            setProcessing(true);
            setMessage(null);
            await API.post('/admin/student-import/reset');
            setMessage({ type: 'success', text: t('pages.studentImport.resetSuccess') });
            loadStatus();
        } catch (error) {
            console.error('Failed to reset tracker:', error);
            setMessage({ type: 'error', text: apiErrorMessage(error, t('pages.studentImport.resetFailed')) });
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <ListPageLayout className="max-w-4xl mx-auto">
                <ListPageHeader
                    title={t('pages.studentImport.title')}
                    subtitle={t('pages.studentImport.loadingSubtitle')}
                    showBack={false}
                />
                <ListLoadingSpinner />
            </ListPageLayout>
        );
    }

    return (
        <ListPageLayout className="max-w-4xl mx-auto">
            <ListPageHeader
                title={t('pages.studentImport.title')}
                subtitle={t('pages.studentImport.subtitle')}
            />

            {message && (
                <div className={`mb-6 p-4 rounded-lg ${message.type === 'error'
                    ? 'bg-red-50 border border-red-200 text-red-800'
                    : 'bg-green-50 border border-green-200 text-green-800'
                    }`}>
                    {message.text}
                    {message.type === 'error' && !status && (
                        <div className="mt-3">
                            <Button onClick={loadStatus} className="bg-white border border-red-300 text-red-800 hover:bg-red-50">
                                {t('common.retry', { defaultValue: 'Retry' })}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {status && (
            <>
            {/* Status Card */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    {t('pages.studentImport.importStatus')}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{t('pages.studentImport.spreadsheetId')}</label>
                        <p className="text-gray-900 font-mono text-sm break-all">{status?.spreadsheetId || '—'}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{t('pages.studentImport.sheetName')}</label>
                        <p className="text-gray-900">{status?.sheetName || '—'}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{t('pages.studentImport.lastProcessedRow')}</label>
                        <p className="text-2xl font-bold text-blue-600">{status?.lastProcessedRow || 0}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{t('pages.studentImport.totalImported')}</label>
                        <p className="text-2xl font-bold text-green-600">{status?.totalImported || 0}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{t('pages.studentImport.lastImportTime')}</label>
                        <p className="text-gray-900">{formatDateTime(status?.lastImportTime)}</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">{t('pages.studentImport.lastUpdated')}</label>
                        <p className="text-gray-900">{formatDateTime(status?.updated)}</p>
                    </div>
                </div>

                {status?.lastError && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <label className="block text-sm font-medium text-red-800 mb-2">{t('pages.studentImport.lastError')}</label>
                        <p className="text-red-700 text-sm font-mono whitespace-pre-wrap">{status.lastError}</p>
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">{t('pages.studentImport.actions')}</h2>

                <div className="space-y-4">
                    <div className="flex items-start gap-4">
                        <Button
                            onClick={handleTriggerImport}
                            disabled={processing}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            {processing ? t('pages.studentImport.processing') : `🔄 ${t('pages.studentImport.triggerImport')}`}
                        </Button>
                        <div className="flex-1">
                            <p className="text-sm text-gray-600">
                                {t('pages.studentImport.triggerDesc')}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-start gap-4">
                        <Button
                            onClick={handleResetTracker}
                            disabled={processing}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            {processing ? t('pages.studentImport.processing') : `⚠️ ${t('pages.studentImport.resetTracker')}`}
                        </Button>
                        <div className="flex-1">
                            <p className="text-sm text-gray-600">
                                {t('pages.studentImport.resetDesc')}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-start gap-4">
                        <Button
                            onClick={loadStatus}
                            disabled={processing}
                            className="bg-gray-600 hover:bg-gray-700 text-white"
                        >
                            🔃 {t('pages.studentImport.refreshStatus')}
                        </Button>
                        <div className="flex-1">
                            <p className="text-sm text-gray-600">
                                {t('pages.studentImport.refreshDesc')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            </>
            )}

            {/* Configuration Info */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">📋 {t('pages.studentImport.configTitle')}</h3>
                <ul className="space-y-2 text-sm text-blue-800">
                    <li>• <Trans i18nKey="pages.studentImport.configDaily" components={{ strong: <strong /> }} /></li>
                    <li>• {t('pages.studentImport.configNewRows')}</li>
                    <li>• {t('pages.studentImport.configRoleEmail')}</li>
                    <li>• {t('pages.studentImport.configUsername')}</li>
                    <li>• <Trans i18nKey="pages.studentImport.configMappings" components={{ code: <code className="bg-blue-100 px-1 rounded" /> }} /></li>
                    <li>• <Trans i18nKey="pages.studentImport.configEnv" components={{ code: <code className="bg-blue-100 px-1 rounded" /> }} /></li>
                </ul>
            </div>

            {/* Setup Guide */}
            <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">🔧 {t('pages.studentImport.setupTitle')}</h3>
                <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside">
                    <li>{t('pages.studentImport.setupStep1')}</li>
                    <li>{t('pages.studentImport.setupStep2')}</li>
                    <li>{t('pages.studentImport.setupStep3')}</li>
                    <li><Trans i18nKey="pages.studentImport.setupStep4" components={{ code: <code className="bg-gray-100 px-1 rounded" /> }} /></li>
                    <li><Trans i18nKey="pages.studentImport.setupStep5" components={{ code: <code className="bg-gray-100 px-1 rounded" /> }} /></li>
                    <li>{t('pages.studentImport.setupStep6')}</li>
                </ol>
            </div>
        </ListPageLayout>
    );
}
